import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { useSessionExpiryChannel } from "./sessionExpiry.js";

import { getMe, login as loginRequest, logout as logoutRequest } from "../api/auth.js";
import { useApiClient } from "../api/ApiClientContext.js";
import type { Capabilities } from "../api/capabilities.js";
import type { WireAccount } from "../api/types.js";
import { describeApiError } from "../domain/errors.js";

/**
 * The session boundary — now backed by real server-side sessions
 * (`apps/server/src/auth/**`) instead of the Phase-4 in-memory stub. The
 * *shape* this exposes is unchanged on purpose: `status` stays exactly
 * `"anonymous" | "authenticated"`, so `RequireAuth` (`status !==
 * "authenticated"` → redirect) and `AppRoutes` (desktop skips `/login`
 * entirely) need no reshaping — that seam was built for this in Phase 4 and
 * this is what it was for.
 *
 * A desktop deployment still never calls the API at all: `status` is pinned
 * to `"authenticated"` immediately, the same as before, because there is
 * still exactly one till and nobody to authenticate against
 * (`capabilities.ts`'s doc comment on `computeCapabilities`).
 *
 * A hosted deployment resolves its session once, on mount (`GET
 * /api/auth/me`), and renders nothing until that settles — the same
 * "gate rendering until known" pattern `App.tsx`'s `CapabilitiesBoundary`
 * already uses for capabilities. That is what keeps `status` a clean
 * two-value union from `RequireAuth`'s point of view: by the time
 * `children` (routes) ever render, the session is already known one way or
 * the other, so there is no third "still checking" state for a consumer of
 * this context to have to handle.
 */
export type AuthStatus = "anonymous" | "authenticated";

export type LoginResult = { ok: true } | { ok: false; message: string };

export interface AuthContextValue {
  status: AuthStatus;
  /** `null` on desktop (no accounts exist there) or while anonymous. Derived from `account.email` — the top bar greeting only ever needs the email, not the whole record. */
  displayName: string | null;
  /** The full account record once authenticated — `null` on desktop and while anonymous. Used for role checks (`RequireAdmin`) and the forced password-change gate. */
  account: WireAccount | null;
  login(email: string, password: string): Promise<LoginResult>;
  logout(): Promise<void>;
  /** Applies a fresh account record after a self-service change (`ForcedPasswordChange`) without a full page reload or re-fetch. */
  applyAccountUpdate(account: WireAccount): void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

type ResolvedStatus = "anonymous" | "authenticated";

export function AuthProvider({ capabilities, children }: { capabilities: Capabilities; children: ReactNode }) {
  const client = useApiClient();
  const isDesktop = capabilities.deployment === "desktop";

  // `null` here specifically means "not settled yet" (hosted only, before
  // the initial `/api/auth/me` resolves) — never exposed as a context value,
  // only used to decide whether to render `children` at all. See the
  // module doc comment for why this keeps `status` a clean two-value union
  // for everything downstream.
  const [resolved, setResolved] = useState<ResolvedStatus | null>(isDesktop ? "authenticated" : null);
  const [account, setAccount] = useState<WireAccount | null>(null);
  const expiryChannel = useSessionExpiryChannel();

  useEffect(() => {
    if (isDesktop) return;
    let cancelled = false;
    getMe(client)
      .then((res) => {
        if (cancelled) return;
        setAccount(res.account);
        setResolved("authenticated");
      })
      .catch(() => {
        if (cancelled) return;
        setAccount(null);
        setResolved("anonymous");
      });
    return () => {
      cancelled = true;
    };
    // Intentionally empty besides `client`/`isDesktop`: this checks the
    // session exactly once per mount, the same as `CapabilitiesBoundary`
    // fetches capabilities once. A session that expires later is caught by
    // the effect below instead.
  }, [client, isDesktop]);

  // A session can expire mid-shift — the operator is mid-list when the cookie
  // lapses. Without this, that surfaced as one broken panel wherever the
  // request happened to be made, with no explanation and no way back. The API
  // client reports any non-auth 401 through this channel; treating it as a
  // logout returns them to the login screen, where the situation is obvious.
  //
  // Desktop never registers: that build has no session to expire, and its
  // 401s (a bad employee PIN) must not be mistaken for one.
  useEffect(() => {
    if (isDesktop || !expiryChannel) return;
    expiryChannel.onExpired = () => {
      setAccount(null);
      setResolved("anonymous");
    };
    return () => {
      expiryChannel.onExpired = null;
    };
  }, [expiryChannel, isDesktop]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status: resolved === "authenticated" ? "authenticated" : "anonymous",
      account,
      displayName: account?.email ?? null,
      async login(email, password) {
        try {
          const res = await loginRequest(client, email, password);
          setAccount(res.account);
          setResolved("authenticated");
          return { ok: true };
        } catch (error) {
          return { ok: false, message: describeApiError(error) };
        }
      },
      async logout() {
        try {
          await logoutRequest(client);
        } catch {
          // Best-effort: even if the request itself fails (e.g. the session
          // was already gone), the user's intent is to end up logged out —
          // clear local state regardless rather than strand them on a
          // "logout failed" dead end.
        }
        setAccount(null);
        setResolved("anonymous");
      },
      applyAccountUpdate(next) {
        setAccount(next);
      },
    }),
    [client, resolved, account],
  );

  if (resolved === null) {
    return (
      <div className="app-boundary-state" role="status">
        Nalaganje …
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
